import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eraser, Download } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

const MandalaMaker = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [symmetry, setSymmetry] = useState(8);
  const [color, setColor] = useState('#DDA0DD');
  const [lineWidth, setLineWidth] = useState(2);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FDFBF7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const resize = () => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCanvas.getContext('2d').drawImage(canvas, 0, 0);
      
      const offsetX = (window.innerWidth - canvas.width) / 2;
      const offsetY = (window.innerHeight - canvas.height) / 2;

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      ctx.fillStyle = '#FDFBF7';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(tempCanvas, offsetX, offsetY);
    };
    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  const drawLine = (x1, y1, x2, y2) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let i = 0; i < symmetry; i++) {
      const angle = (Math.PI * 2 * i) / symmetry;
      
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angle);
      
      ctx.beginPath();
      ctx.moveTo(x1 - centerX, y1 - centerY);
      ctx.lineTo(x2 - centerX, y2 - centerY);
      ctx.stroke();
      
      // Mirror reflection
      ctx.scale(-1, 1);
      ctx.beginPath();
      ctx.moveTo(x1 - centerX, y1 - centerY);
      ctx.lineTo(x2 - centerX, y2 - centerY);
      ctx.stroke();
      
      ctx.restore();
    }
  };

  const getCanvasPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e) => {
    setIsDrawing(true);
    lastPosRef.current = getCanvasPos(e);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;

    const pos = getCanvasPos(e);
    drawLine(lastPosRef.current.x, lastPosRef.current.y, pos.x, pos.y);
    lastPosRef.current = pos;
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FDFBF7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `mandala-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#F8E8FF] relative overflow-hidden">
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="absolute inset-0 cursor-crosshair"
        style={{ touchAction: 'none' }}
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Mandala Maker</h1>
          
          <div className="flex gap-3">
            <button
              data-testid="download-mandala"
              onClick={downloadImage}
              className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Save
            </button>
            <button
              data-testid="clear-canvas"
              onClick={clearCanvas}
              className="bg-white border-2 border-[#81B29A] text-[#81B29A] px-6 py-3 rounded-full font-semibold hover:bg-[#81B29A]/10 transition-all duration-300 flex items-center gap-2"
            >
              <Eraser className="w-5 h-5" />
              Clear
            </button>
          </div>
        </header>

        <div className="fixed left-8 top-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 space-y-6 max-w-xs">
          <div>
            <label className="block text-sm font-semibold text-[#2D3748] mb-3">
              Symmetry: {symmetry}x
            </label>
            <Slider
              data-testid="symmetry-slider"
              value={[symmetry]}
              onValueChange={(val) => setSymmetry(val[0])}
              min={3}
              max={24}
              step={1}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#2D3748] mb-3">
              Brush Size: {lineWidth}px
            </label>
            <Slider
              data-testid="brush-size-slider"
              value={[lineWidth]}
              onValueChange={(val) => setLineWidth(val[0])}
              min={1}
              max={20}
              step={1}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#2D3748] mb-3">
              Color
            </label>
            <div className="grid grid-cols-4 gap-2">
              {['#DDA0DD', '#81B29A', '#F2CC8F', '#E07A5F', '#A8DADC', '#FF6B9D', '#4B0082', '#20B2AA'].map(c => (
                <button
                  key={c}
                  data-testid={`color-${c}`}
                  onClick={() => setColor(c)}
                  className={`w-12 h-12 rounded-xl transition-all ${
                    color === c ? 'ring-4 ring-[#81B29A] scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MandalaMaker;